/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { Accordion, Actionable, Text, View } from '@az/starc-ui';
import TableOfContentsStyles from './TableOfContents.module.scss';
import cx from 'classnames';
import { type HeadingStructureItem } from '../../hooks/useHeadingStructure';
import { type RefObject } from 'react';

const HEADER_HEIGHT = 100;

type ParentItemProps = {
  heading: HeadingStructureItem;
  contentRef: RefObject<HTMLDivElement | null>;
};

export const GroupHeading = ({ heading, contentRef }: ParentItemProps) => {
  const { elementText, subHeadings } = heading;

  const handleScrollToElement = (tag: 'h2' | 'h3', elementText: string) => {
    if (!contentRef.current) {
      return;
    }

    const headings = Array.from(contentRef.current.querySelectorAll(tag));
    const heading = headings.find((h) => (h.textContent || '') === elementText);

    if (heading) {
      scrollTo({
        top: (heading as HTMLElement).offsetTop - HEADER_HEIGHT,
      });
    }
  };

  return (
    <Accordion
      headerOptions={{
        headerElement: (
          <Text
            weight="bold"
            // we want to scroll to the heading, but we don't scroll when opening the Accordion
            // we also cannot wrap the Text in an actionable component because Accordion
            // already does it and it will create nested button elements
            attributes={{ onClick: () => handleScrollToElement('h2', elementText) }}
            className={TableOfContentsStyles.groupHeading}
          >
            {elementText}
          </Text>
        ),
      }}
      name={`${elementText}~${subHeadings.length}`}
      className={cx({ [TableOfContentsStyles.noLinksSection]: subHeadings.length === 0 })}
    >
      <View direction="column">
        {subHeadings.map((subHeading) => (
          <Actionable key={subHeading} onClick={() => handleScrollToElement('h3', subHeading)}>
            <Text size="087" lineHeight="140">
              {subHeading}
            </Text>
          </Actionable>
        ))}
      </View>
    </Accordion>
  );
};
